import React, { useCallback, useEffect, useRef } from 'react';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  HelpOutlined as QuestionIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useJotCoachTracking } from '@services/statsig';
import { useModal } from '@store/hooks';

import { MODAL_TYPES } from '../../../components/modals/constants';
import type { JotCoachProviderQuestion } from '../store/hooks';

interface ProviderQuestionItemProps {
  question: JotCoachProviderQuestion;
  sessionId?: string;
  noteId?: string;
  sectionId?: string;
  onSubmitAnswer?: (question: JotCoachProviderQuestion, answer: string) => void;
}

export const ProviderQuestionItem: React.FC<ProviderQuestionItemProps> = ({
  question,
  sessionId,
  noteId,
  sectionId,
}) => {
  const { open } = useModal();
  const {
    trackQuestionRendered,
    trackQuestionViewed,
    trackFeedbackUseful,
    trackFeedbackNotUseful,
    trackFeedbackComment,
  } = useJotCoachTracking();

  // Refs for tracking deduplication
  const hasBeenRenderedRef = useRef(false);
  const hasBeenViewedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track render when card enters viewport
  useEffect(() => {
    if (!sessionId || !noteId || !sectionId || hasBeenRenderedRef.current || !cardRef.current)
      return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasBeenRenderedRef.current) {
            trackQuestionRendered({
              jotcoach_session_id: sessionId,
              note_id: noteId,
              question_id: question.id,
              section_id: sectionId,
              question_title: question.question,
            });
            hasBeenRenderedRef.current = true;
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [sessionId, noteId, sectionId, question.id, question.question, trackQuestionRendered]);

  // Track hover/view
  const handleMouseEnter = useCallback(() => {
    if (!sessionId || !noteId || !sectionId || hasBeenViewedRef.current) return;

    trackQuestionViewed({
      jotcoach_session_id: sessionId,
      note_id: noteId,
      question_id: question.id,
      section_id: sectionId,
      question_title: question.question,
    });
    hasBeenViewedRef.current = true;
  }, [sessionId, noteId, sectionId, question.id, question.question, trackQuestionViewed]);

  // Track feedback clicks
  const handleFeedbackUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'question',
          item_id: question.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'question',
        itemId: question.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: true,
        itemContent: {
          title: question.question,
          description: question.why_needed,
        },
      });
    },
    [
      sessionId,
      noteId,
      sectionId,
      question.id,
      question.question,
      question.why_needed,
      trackFeedbackUseful,
      open,
    ],
  );

  const handleFeedbackNotUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackNotUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'question',
          item_id: question.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'question',
        itemId: question.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: false,
        itemContent: {
          title: question.question,
          description: question.why_needed,
        },
      });
    },
    [
      sessionId,
      noteId,
      sectionId,
      question.id,
      question.question,
      question.why_needed,
      trackFeedbackNotUseful,
      open,
    ],
  );

  const handleFeedbackComment = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId && sectionId) {
        trackFeedbackComment({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'question',
          item_id: question.id,
          section_id: sectionId,
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'question',
        itemId: question.id,
        sessionId,
        noteId,
        sectionId,
        initialUseful: null,
        itemContent: {
          title: question.question,
          description: question.why_needed,
        },
      });
    },
    [
      sessionId,
      noteId,
      sectionId,
      question.id,
      question.question,
      question.why_needed,
      trackFeedbackComment,
      open,
    ],
  );

  return (
    <Box
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'info.light',
        borderLeftWidth: 4,
        '&:hover': {
          borderColor: 'info.main',
          boxShadow: 1,
        },
        transition: 'all 0.2s ease',
        position: 'relative',
      }}
    >
      {/* Feedback buttons */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
          zIndex: 1,
        }}
      >
        <Tooltip title="Mark as useful">
          <IconButton
            size="small"
            onClick={handleFeedbackUseful}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'success.light', color: 'success.dark' },
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mark as not useful">
          <IconButton
            size="small"
            onClick={handleFeedbackNotUseful}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'error.dark' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add feedback">
          <IconButton
            size="small"
            onClick={handleFeedbackComment}
            sx={{
              p: 0.5,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'info.light', color: 'info.dark' },
            }}
          >
            <CommentIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 7 }}>
        <QuestionIcon sx={{ color: 'info.main', mt: 0.5, fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {question.question}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {question.why_needed}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
